import { Box, Typography } from "@mui/material";

const createTooltipContent = (title, items) => (
  <Box sx={{ maxWidth: 400 }}>
    <Typography variant="body2" paragraph sx={{ mb: 1, color: "inherit" }}>
      {title}
    </Typography>
    <Box component="ul" sx={{ m: 0, pl: 2 }}>
      {items.map(({ label, description, subItems }, index) => (
        <li key={index}>
          <Typography variant="body2" sx={{ mb: 0.5, color: "inherit" }}>
            <b>{label}</b>: {description}
            {subItems && (
              <Box component="ul" sx={{ mt: 0.5, mb: 1 }}>
                {subItems.map((item, subIndex) => (
                  <li key={subIndex}>
                    <Typography variant="body2" sx={{ color: "inherit" }}>
                      {item}
                    </Typography>
                  </li>
                ))}
              </Box>
            )}
          </Typography>
        </li>
      ))}
    </Box>
  </Box>
);

export const COLUMN_TOOLTIPS = {
  AVERAGE: createTooltipContent("Average score across all EEG benchmarks:", [
    {
      label: "Calculation",
      description: "Mean accuracy across all downstream EEG datasets",
      subItems: [
        "Each benchmark score is accuracy normalized to 0-100",
        "All benchmark scores are averaged together",
      ],
    },
  ]),

  BCIC2A: createTooltipContent("BCI Competition IV Dataset 2a (BCIC-2a):", [
    {
      label: "Task",
      description: "4-class motor imagery classification",
      subItems: [
        "Classes: left hand, right hand, feet, tongue",
        "9 subjects, 22 EEG channels, 200 Hz",
        "4-second trials with event-related windowing",
      ],
    },
    {
      label: "Scoring: Accuracy",
      description: "Proportion of correctly classified motor imagery trials.",
    },
  ]),

  PHYSIONET: createTooltipContent("PhysioNet Motor Imagery (PhysioNet MI):", [
    {
      label: "Task",
      description: "4-class motor imagery classification",
      subItems: [
        "Classes: left hand, right hand, feet, both hands",
        "109 subjects, 64 EEG channels, 200 Hz",
        "3-second trials with event-related windowing",
      ],
    },
    {
      label: "Scoring: Accuracy",
      description: "Proportion of correctly classified imagery trials.",
    },
  ]),

  ISRUC_SLEEP: createTooltipContent("ISRUC-SLEEP Group I:", [
    {
      label: "Task",
      description: "5-class sleep staging",
      subItems: [
        "Classes: Wake, N1, N2, N3, REM",
        "~100 subjects, 6 EEG channels, 200 Hz",
        "30-second epoch windows",
      ],
    },
    {
      label: "Scoring: Accuracy",
      description: "Proportion of correctly classified 30s sleep epochs.",
    },
  ]),

  TUAB: createTooltipContent("TUH EEG Abnormal Corpus (TUAB v3.0.1):", [
    {
      label: "Task",
      description: "Binary pathology detection (normal vs abnormal)",
      subItems: [
        "Classes: normal, abnormal",
        "290+ subjects, 16 EEG channels, 200 Hz",
        "10-second fixed-length windows",
      ],
    },
    {
      label: "Scoring: Accuracy",
      description: "Proportion of correctly classified recordings.",
    },
  ]),

  TUEV: createTooltipContent("TUH EEG Events Corpus (TUEV v2.0.1):", [
    {
      label: "Task",
      description: "6-class EEG event classification",
      subItems: [
        "Classes: SPSW, GPED, PLED, EYEM, ARTF, BCKG",
        "200+ subjects, 21 EEG channels, 200 Hz",
        "5-second event windows",
      ],
    },
    {
      label: "Scoring: Accuracy",
      description: "Proportion of correctly classified EEG events.",
    },
  ]),

  CHBMIT: createTooltipContent("CHB-MIT Scalp EEG Database:", [
    {
      label: "Task",
      description: "Binary seizure detection",
      subItems: [
        "Classes: seizure, background (non-seizure)",
        "23 pediatric subjects, 17 EEG channels, 200 Hz",
        "10-second windows from bipolar montage",
      ],
    },
    {
      label: "Scoring: Accuracy",
      description: "Proportion of correctly detected seizure and background windows.",
    },
  ]),

  FACED: createTooltipContent("FACED Emotion Recognition Dataset:", [
    {
      label: "Task",
      description: "9-class discrete emotion recognition from EEG",
      subItems: [
        "Classes: Anger, Disgust, Fear, Sadness, Neutral, Amusement, Inspiration, Joy, Tenderness",
        "123 subjects, 26 EEG channels, 200 Hz",
        "10-second event-related windows",
      ],
    },
    {
      label: "Scoring: Accuracy",
      description: "Proportion of correctly classified emotion labels.",
    },
  ]),

  SEEDV: createTooltipContent("SEED-V Emotion Recognition Dataset:", [
    {
      label: "Task",
      description: "5-class emotion recognition from EEG",
      subItems: [
        "Classes: Happy, Sad, Neutral, Disgust, Fear",
        "62 EEG channels, 200 Hz",
        "1-second windows, session-based splits",
      ],
    },
    {
      label: "Scoring: Accuracy",
      description: "Proportion of correctly classified emotion labels.",
    },
  ]),

  ARCHITECTURE: createTooltipContent("EEG Foundation Model:", [
    {
      label: "Definition",
      description: "The pre-trained foundation model that is being adapted",
      subItems: [
        "LaBraM: Vision Transformer for EEG (12 layers, 200D embedding)",
        "EEGPT: Transformer with patch-based tokenization (8 layers, 512D)",
        "BIOT: Linear Attention Transformer (4 layers, 256D)",
        "BENDR: CNN + BERT-inspired Transformer (8 layers, 512D encoder)",
        "SignalJEPA: CNN + Transformer with JEPA-style predictive learning",
        "CBraMod: Criss-Cross Transformer with separate spatial/temporal attention (12 layers, 200D)",
        "REVE: Vision Transformer with GEGLU (22 layers, 512D)",
      ],
    },
  ]),

  PRECISION: createTooltipContent("Numerical Precision Format:", [
    {
      label: "Overview",
      description:
        "Data format used to store model weights and perform computations",
      subItems: [
        "bfloat16: Half precision (Brain Float format), good for stability",
        "float16: Half precision",
        "8bit/4bit: Quantized formats, for efficiency",
      ],
    },
  ]),

  FLAGS: createTooltipContent("Model Flags:", [
    {
      label: "Filters",
      subItems: [
        "Unavailable: No longer on the hub (private, deleted) or missing a license tag",
      ],
    },
  ]),

  PARAMETERS: createTooltipContent("Model Parameters:", [
    {
      label: "Measurement",
      description: "Total number of trainable parameters in millions",
      subItems: [
        "Foundation model total parameters (before adapter)",
        "Adapter methods reduce trainable params by 90-99.5%",
        "LoRA r=16: ~98% reduction",
        "IA3: ~99.5% reduction (only scaling vectors)",
      ],
    },
  ]),

  LICENSE: createTooltipContent("Model License Information:", [
    {
      label: "Importance",
      description: "Legal terms governing model usage and distribution",
      subItems: [
        "Commercial vs non-commercial use",
        "Attribution requirements",
        "Modification and redistribution rights",
      ],
    },
  ]),
};

export const UI_TOOLTIPS = {
  COLUMN_SELECTOR: "Choose which columns to display in the table",
  DISPLAY_OPTIONS: createTooltipContent("Table Display Options", [
    {
      label: "Overview",
      description: "Configure how the table displays data and information",
      subItems: [
        "Row size and layout",
        "Score display format",
        "Ranking calculation",
        "Average score computation",
      ],
    },
  ]),
  SEARCH_BAR: createTooltipContent("Advanced Model Search", [
    {
      label: "Name Search",
      description: "Search directly by model name",
      subItems: [
        "Supports regular expressions (e.g., ^labram.*lora)",
        "Case sensitive",
      ],
    },
    {
      label: "Field Search",
      description: "Use @field:value syntax for precise filtering",
      subItems: [
        "@architecture:labram - Filter by foundation model",
        "@license:mit - Filter by license",
        "@precision:float16 - Filter by precision",
        "@type:lora - Filter by adapter method",
      ],
    },
    {
      label: "Multiple Searches",
      description: "Combine multiple criteria using semicolons",
      subItems: [
        "labram @type:lora; @architecture:eegpt",
        "^biot.*ia3; @precision:float16",
      ],
    },
  ]),
  QUICK_FILTERS: createTooltipContent(
    "Filter models based on their total parameter count:",
    [
      {
        label: "Tiny (Up to 10M)",
        description:
          "Lightweight models suitable for edge devices and quick experiments.",
      },
      {
        label: "Small (10M-50M)",
        description:
          "Compact models balancing performance and efficiency (e.g., CBraMod ~4M, EEGPT ~10M).",
      },
      {
        label: "Medium (50M-200M)",
        description:
          "Mid-range foundation models with good capacity.",
      },
      {
        label: "Large (200M+)",
        description:
          "Large foundation models offering broad generalization (e.g., REVE, LaBraM).",
      },
    ]
  ),
  ROW_SIZE: {
    title: "Row Size",
    description:
      "Adjust the height of table rows. Compact is ideal for viewing more data at once, while Large provides better readability and touch targets.",
  },
  SCORE_DISPLAY: {
    title: "Score Display",
    description:
      "Choose between normalized scores (0-100% scale for easy comparison) or raw scores (actual accuracy values 0-1). Normalized scores help compare performance across different benchmarks.",
  },
  RANKING_MODE: {
    title: "Ranking Mode",
    description:
      "Choose between static ranking (original position in the full leaderboard) or dynamic ranking (position based on current filters and sorting).",
  },
  AVERAGE_SCORE: {
    title: "Average Score Calculation",
    description:
      "Define how the average score is calculated. 'All Scores' uses all benchmarks, while 'Visible Only' calculates the average using only the visible benchmark columns.",
  },
};

export const getTooltipStyle = {};

export const TABLE_TOOLTIPS = {
  HUB_LINK: (modelName) => `View ${modelName} on Hugging Face Hub`,
  EVAL_RESULTS: (modelName) =>
    `View detailed evaluation results for ${modelName}`,
  POSITION_CHANGE: (change) =>
    `${Math.abs(change)} position${Math.abs(change) > 1 ? "s" : ""} ${
      change > 0 ? "up" : "down"
    }`,
  METADATA: {
    TYPE: (type) => type || "-",
    ARCHITECTURE: (arch) => arch || "-",
    PRECISION: (precision) => precision || "-",
    LICENSE: (license) => license || "-",
    UPLOAD_DATE: (date) => date || "-",
    SUBMISSION_DATE: (date) => date || "-",
    BASE_MODEL: (model) => model || "-",
  },
};
